// LinkedIn caps the connection note at 300 chars. The backend appends the
// data-room link before sending (mirrors apps/backend/src/constants/heyreach.ts:
// `assembleMsg1WithLink` → prefix + `<studio-origin>/s/<12-char token>`).
export const MSG1_MAX_CHARS = 300
// msg1: LLM emits its own CTA; backend appends just ` <link>` (single space).
const MSG1_LINK_PREFIX = ' '
const MSG1_LINK_PATH = '/s/'
const MSG1_TOKEN_LENGTH = 12

export function estimateMsg1LinkLength(): number {
  const origin = typeof window === 'undefined' ? '' : window.location.origin
  return MSG1_LINK_PREFIX.length + origin.length + MSG1_LINK_PATH.length + MSG1_TOKEN_LENGTH
}

// Conservative per-token expansion estimates, mirroring the backend validator
// (apps/engine/.../contact_messages.py `_effective_len`) so the founder is
// warned before a substituted note overflows LinkedIn's cap. [Link] is sized
// from the real studio origin via estimateMsg1LinkLength().
const MSG1_TOKEN_ESTIMATES = {
  '[FundName]': 25,
  '[FundFocus]': 25,
  '[FundSector]': 20,
  firstName: 10,
} as const

function countOccurrences(text: string, token: string): number {
  return token ? text.split(token).length - 1 : 0
}

/**
 * Estimate msg1's length as the investor will receive it: the raw template with
 * every placeholder token ([firstName]/[FundName]/[FundFocus]/[FundSector]/[Link])
 * swapped
 * for its runtime-size estimate. A legacy (tokenless) msg1 has the link appended
 * by the backend, so it always counts one link. Used to drive the 300-char
 * connection-note warning — it must not under-count, or the founder ships a note
 * that overflows once tokens expand.
 */
export function estimateMsg1SentLength(body: string): number {
  let total = body.length
  const firstNameCount = (body.match(/\[firstName\]/gi) ?? []).length
  total += firstNameCount * (MSG1_TOKEN_ESTIMATES.firstName - '[firstName]'.length)
  for (const token of ['[FundName]', '[FundFocus]', '[FundSector]'] as const) {
    total += countOccurrences(body, token) * (MSG1_TOKEN_ESTIMATES[token] - token.length)
  }
  const linkCount = countOccurrences(body, '[Link]')
  const linkLen = estimateMsg1LinkLength()
  // Inline [Link] token(s) swap for the link; a legacy body has it appended.
  total += linkCount > 0 ? linkCount * (linkLen - '[Link]'.length) : linkLen
  return total
}
